// ══════════════════════════════════════════════════
// MARK: Setup
// ──────────────────────────────────────────────────
// #═#═#═#═#═# Imports #═#═#═#═#═#
import { CSSRuleSet } from '../../ts/class/style/exportlist';
import { debounce } from '../../ts/module/timing';
import styles from './style.scss?inline';


// ══════════════════════════════════════════════════
// MARK: Icon Element
// ──────────────────────────────────────────────────
export default class IconElement extends HTMLElement {
	// #═#═#═#═#═# Properties #═#═#═#═#═#
		// #════ Initialized ════#
	/**	Shadow DOM of the Element */
	public shadowRoot			=	this.attachShadow({mode: 'open'});
	/**	Internal Stylesheet for passing CSS Variables */
	private stylesheet			=	new CSSRuleSet(styles);
	/**	Debounced Update Function */
	// private update				=	debounce(this.__update, 50);


		// #════ Static ════#
	/** Default Font */
	static defaultFont = 'fas';
	/**	Font Presets for easy use of common icon fonts */
	static fontPresets = new Map<string, string|(() => string)>([
		["fas", "var(--cvh-font-icon-fas)"],
		["far", "var(--cvh-font-icon-far)"],
		["fab", "var(--cvh-font-icon-fab)"],
	]);

	/**	Observed Attributes for the Element */
	static	observedAttributes	=	["character", "type", "font", "color"];


	// #═#═#═#═#═# Constructor #═#═#═#═#═#
	constructor() {
		// #════ Parent ════#
		super();


		// #════ Properties ════#
		this.shadowRoot.adoptedStyleSheets		=	[this.stylesheet.getStylesheet()];


		// #════ Build ════#
		this.render();
	}


	//
	// MARK: Render
	//
	// #═#═#═#═#═# Build #═#═#═#═#═#
	/**
	 * Builds the ShadowRoot HTML of the element.
	 */
	private render(
	): void {
		this.shadowRoot.innerHTML = `
			<div class="view" part="view">
				<slot></slot>
			</div>
		`;
	}


	//
	// MARK: Lifecycle
	//
	// #═#═#═#═#═# Connected Callback #═#═#═#═#═#
	/**
	 * Is called when the element is connected to the DOM.
	 * @access	public
	 * @return	{void}
	 */
	// public connectedCallback(
	// ): void {
	// 	this.update();
	// }


	// #═#═#═#═#═# Attribute Changed #═#═#═#═#═#
	public attributeChangedCallback(
		name: string,
		oldValue: string | null,
		newValue: string | null
	): void {
		// #════ Guard ════#
		if(newValue === oldValue) {
			return;
		}


		// #════ Update ════#
		switch(name) {
			case 'character':
				this.#setCharacter(newValue ?? undefined, this.type ?? 'unicode');
				this.#setFont(this.font ?? IconElement.defaultFont);
				break;

			case 'type':
				if(newValue === 'svg') {
					this.#clearProperties();
					return;
				} else {
					this.#setCharacter(newValue ?? undefined, newValue ?? 'unicode');
					this.#setFont();
					this.#setColor();
				}
				break;

			case 'font':
				this.#setFont(newValue ?? IconElement.defaultFont);
				break;

			case 'color':
				this.#setColor(newValue ?? undefined);
				break;
		}
	}


	//
	// MARK: Methods
	//
	// #═#═#═#═#═# Set Icon #═#═#═#═#═#
	/**
	 * Set the Icon of the Element via Object declaration.
	 * @param props 
	 * @returns 
	 */
	public setIcon(
		props: {character?: string, font?: string, type?: string} | undefined
	): void {
		// #════ Guard ════#
		if(!props) {
			return;
		}


		// #════ Destruct ════#
		const { character, font, type } = props;


		// #════ Set Attributes ════#
		if(type !== undefined) {
			this.type		=	type;
		}
		if(character !== undefined) {
			this.character	=	character;
		}
		if(font !== undefined) {
			this.font		=	font;
		}
	}


	// #═#═#═#═#═# Validate Character #═#═#═#═#═#
	private validateCharacter(
		character?: string,
		type: string = this.type ?? 'unicode'
	): string | void {
		// #════ Guard ════#
		if(!character) {
			return;
		}


		// #════ Sanitise ════#
		const withSlash = character.startsWith('\\');

		// Unicode
		if(type === 'unicode' && !withSlash) {
			character = `\\${character}`;
		}

		// Glyph
		else if(type === 'glyph' && withSlash) {
			character = character.slice(1);
		}


		// #════ Correct Quatations ════#
		switch(type) {
			case 'unicode':
			case 'glyph':
				if(!character.startsWith('"') && !character.startsWith("'")) {
					return `"${character}"`;
				}
		}


		// #════ Return ════#
		return character;
	}


	// #═#═#═#═#═# Validate Font #═#═#═#═#═#
	/**
	 * Validates the Font of the Character
	 */
	private validateFont(
		font?: string
	): string | void {
		// #════ Guard ════#
		if(!font) {
			return;
		}


		// #════ Font Presets ════#
		if(IconElement.fontPresets.has(font)) {
			const preset = IconElement.fontPresets.get(font);
			if(typeof preset === 'function') {
				return preset();
			} else if(typeof preset === 'string') {
				return preset;
			}

			throw new Error(`Font presets must be a string or a function returning a string.`);
		}

		
		// #════ CSS Variable Shorthand ════#
		if(font && font.startsWith('--')) {
			return	`var(${font})`;
		}


		// #════ Return Font ════#
		return font;
	}


	// #═#═#═#═#═# Write to CSS #═#═#═#═#═#
	/**
	 * Passes values to CSS Variables
	 */
	#writeVariables() {
		


		// #════ Validate ════#
		const color = this.color;


		// #════ Write Lines ════#
		if(color)
			this.stylesheet.setProperty(':host', '--_icon-color', color);
	}


	// #═#═#═#═#═# Unset Properties #═#═#═#═#═#
	/**
	 * Clear the CSS properties set by the element.
	 */
	#clearProperties(
	): void {
		this.stylesheet.removeProperty(':host', '--_icon-character');
		this.stylesheet.removeProperty(':host', '--_icon-font');
		this.stylesheet.removeProperty(':host', '--_icon-color');
	}


	// #═#═#═#═#═# Set Character #═#═#═#═#═#
	/**
	 * Sets the Character of the Icon Element.
	 * @param character 
	 * @param type 
	 */
	#setCharacter(
		character: string|undefined = this.character,
		type: string = this.type ?? 'unicode'
	): void {
		// #════ Validate ════#
		const valid = this.validateCharacter(character, type);
		if(valid) {
			this.stylesheet.setProperty(':host', '--_icon-character', valid);
		}
	}


	// #═#═#═#═#═# Set Font #═#═#═#═#═#
	/**
	 * Sets the Font of the Icon Element.
	 * @param font
	 */
	#setFont(
		font: string|undefined = this.font
	): void {
		// #════ Validate ════#
		const valid = this.validateFont(font);
		if(valid) {
			this.stylesheet.setProperty(':host', '--_icon-font', valid);
		}
	}


	// #═#═#═#═#═# Set Color #═#═#═#═#═#
	/**
	 * Sets the Color of the Icon Element.
	 * @param color 
	 */
	#setColor(
		color: string|undefined = this.color
	): void {
		if(color)
			this.stylesheet.setProperty(':host', '--_icon-color', color);
		else
			this.stylesheet.removeProperty(':host', '--_icon-color');
	}


	//
	// MARK: Accessors
	//
	// #═#═#═#═#═# Character #═#═#═#═#═#
	get character() {
		return this.getAttribute('character') ?? undefined;
	}

	set character(value: string | undefined) {
		if(value)
			this.setAttribute('character', value);
		else
			this.removeAttribute('character');
	}


	// #═#═#═#═#═# Type #═#═#═#═#═#
	get type() {
		if(this.children.length > 0) {
			return 'svg';
		}

		return this.getAttribute('type') ?? undefined;
	}
	
	set type(value: string | undefined) {
		if(value)
			this.setAttribute('type', value);
		else
			this.removeAttribute('type');
	}


	// #═#═#═#═#═# Font #═#═#═#═#═#
	get font() {
		return this.getAttribute('font') ?? 'fas';
	}

	set font(value: string | undefined) {
		if(value)
			this.setAttribute('font', value);
		else
			this.removeAttribute('font');
	}


	// #═#═#═#═#═# Color #═#═#═#═#═#
	get color() {
		return this.getAttribute('color') ?? undefined;
	}
	
	set color(value: string | undefined) {
		if(value)
			this.setAttribute('color', value);
		else
			this.removeAttribute('color');
	}
}


// ══════════════════════════════════════════════════
// MARK: Initialisation
// ──────────────────────────────────────────────────
window.customElements.define('cvh-icon', IconElement);
